/**
 * BodyChartDialog — create or preview a body chart.
 *
 * Two modes:
 *   - ``existing=null``    → create new: title + kind/intensity toolbar
 *                             + BodyChart in interactive mode + save
 *   - ``existing={...}``   → preview only (read-only BodyChart)
 *
 * The parent passes ``onSaved`` to refresh whatever list the chart
 * lives in (timeline, dedicated page, etc.).
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import BodyChart, { BodyChartLegend } from "@/components/BodyChart";
import { useCanonicalClient } from "@/lib/canonicalClient";

const KIND_OPTIONS = [
  ["pain", "Pain"],
  ["swelling", "Swelling"],
  ["numbness", "Numbness"],
  ["tingling", "Tingling"],
  ["scar", "Scar"],
  ["other", "Other"],
];

export default function BodyChartDialog({ email, existing, onClose, onSaved }) {
  const canonical = useCanonicalClient();
  const readOnly = !!existing;
  const [title, setTitle] = useState(existing?.title || "Body chart");
  const [markers, setMarkers] = useState(existing?.markers || []);
  const [kind, setKind] = useState("pain");
  const [intensity, setIntensity] = useState(5);
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api.post(`/health-record/${encodeURIComponent(email)}/body-charts`, {
        title, markers,
        // Phase-2: stamp so the chart is scoped to this canonical client.
        canonical_client_id: canonical?.clientId || null,
      });
      toast.success("Body chart saved");
      onSaved?.();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="body-chart-dialog">
        <DialogHeader>
          <DialogTitle>{readOnly ? "Body chart" : "New body chart"}</DialogTitle>
          <DialogDescription>
            {readOnly
              ? "Preview of the recorded body chart."
              : "Tap the diagram to drop markers. Choose kind and intensity above, then click on the body outline where the finding is located."}
          </DialogDescription>
        </DialogHeader>

        {!readOnly && (
          <div className="space-y-2">
            <div>
              <Label className="text-xs">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="body-chart-title" />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Kind</Label>
                <select
                  value={kind} onChange={(e) => setKind(e.target.value)}
                  className="ml-2 h-8 px-2 border border-zinc-300 rounded text-sm bg-white"
                  data-testid="body-chart-kind"
                >
                  {KIND_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-2xs uppercase tracking-wider text-zinc-500">Intensity</Label>
                <input
                  type="range" min={0} max={10} value={intensity}
                  onChange={(e) => setIntensity(parseInt(e.target.value, 10))}
                  className="ml-2 align-middle"
                  data-testid="body-chart-intensity"
                />
                <span className="ml-2 text-sm font-mono-num tabular-nums">{intensity}/10</span>
              </div>
              <div className="ml-auto"><BodyChartLegend /></div>
            </div>
          </div>
        )}

        <BodyChart
          markers={markers}
          interactive={!readOnly}
          onChange={setMarkers}
          activeKind={kind}
          activeIntensity={intensity}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {!readOnly && (
            <Button onClick={save} disabled={saving} data-testid="body-chart-save">
              {saving ? "Saving…" : "Save chart"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
